import { DOCUMENT } from '@angular/common';
import { inject, Injectable, InjectionToken, signal } from '@angular/core';

/**
 * DI token carrying the Google Tag Manager container ID (e.g. `GTM-XXXXXXX`).
 *
 * A `platform/*` library must never import an app's `environment` (it would
 * reverse the dependency direction), so the container ID is injected instead.
 * The owning app provides it from its own environment config:
 *
 * ```ts
 * { provide: GTM_CONTAINER_ID, useValue: environment.gtm_id }
 * ```
 *
 * When the token is absent (or resolves to `null`/empty), the service becomes
 * a no-op — nothing is injected into the DOM and `pushTag()` does nothing.
 */
export const GTM_CONTAINER_ID = new InjectionToken<string | null>('gtm-container-id');

/**
 * GoogleTagManagerService — browser-only Google Tag Manager (GTM) integration.
 *
 * Lazily injects the GTM container `<script>` into `<head>` and forwards tag
 * objects to `window.dataLayer`. GA4 and e-commerce tags are configured inside
 * the GTM UI, not here — this service only loads the container and pushes
 * events.
 *
 * Consent gating (important):
 * - The service NEVER loads itself. `addGtmToDom()` is the only entry point and
 *   the caller decides when to invoke it (e.g. after the user accepts the
 *   `statistics` cookie category in production).
 * - `pushTag()` is push-only: it forwards to the dataLayer ONLY once the
 *   container is loaded, and is a silent no-op otherwise. This deliberately
 *   diverges from the legacy auto-loading `pushTag()` so events can never
 *   trigger GTM loading behind the consent gate.
 *
 * Source pattern: apps/web-console/src/app/shared/services/google-tag-manager.service.ts
 * Evidence: docs/analytics/google-analytics-gtm.md
 */
@Injectable({ providedIn: 'root' })
export class GoogleTagManagerService {
  private readonly document = inject(DOCUMENT);
  private readonly gtmId = inject(GTM_CONTAINER_ID, { optional: true });

  /** Whether the GTM container script has finished loading. */
  private readonly loaded = signal(false);

  /** Read-only view of the loaded state for consumers that need to react. */
  readonly isLoaded = this.loaded.asReadonly();

  /**
   * Returns (and lazily initialises) `window.dataLayer`.
   * Returns an empty array when no `window` is available (e.g. SSR).
   */
  getDataLayer(): unknown[] {
    const win = this.document.defaultView as (Window & { dataLayer?: unknown[] }) | null;
    if (!win) {
      return [];
    }
    win.dataLayer = win.dataLayer ?? [];
    return win.dataLayer;
  }

  /**
   * Inject the GTM container `<script>` into `<head>`.
   *
   * Idempotent — subsequent calls resolve immediately once loaded. Resolves
   * `false` (without injecting anything) when no container ID is configured.
   * Call this only when allowed to (e.g. after `statistics` consent).
   */
  addGtmToDom(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      if (this.loaded()) {
        return resolve(true);
      }
      if (!this.gtmId) {
        return resolve(false);
      }

      const dataLayer = this.getDataLayer();
      dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

      const script = this.document.createElement('script');
      script.id = 'GTMscript';
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtm.js?id=${this.gtmId}`;
      script.addEventListener('load', () => {
        this.loaded.set(true);
        resolve(true);
      });
      script.addEventListener('error', () => reject(false));
      this.document.head.insertBefore(script, this.document.head.firstChild);
    });
  }

  /**
   * Push a tag/event object onto the dataLayer.
   *
   * Push-only: no-ops unless the container is already loaded (consent-gated).
   * It will NOT load GTM on demand — use `addGtmToDom()` for that.
   */
  pushTag(item: object): void {
    if (!this.gtmId || !this.loaded()) {
      return;
    }
    this.getDataLayer().push(item);
  }

  /**
   * Convenience helper for a route-change page view.
   * No-ops until the container is loaded (consent-gated).
   */
  trackPageView(pagePath: string): void {
    this.pushTag({ event: 'page_view', page_path: pagePath });
  }
}
